<template>
  <v-card flat class="flex-grow-1 d-flex flex-column">
    <v-toolbar v-if="tabData.level !== 2" flat class="flex-grow-0">
      <reports-toolbar-dates
        :start-date="tabData.date"
        @apply-date="getTableItems($event)"
      ></reports-toolbar-dates>
    </v-toolbar>
    <v-data-table
      class="px-3 px-sm-5"
      :class="{
        'full-height-table d-flex flex-column flex-grow-1 pt-0 ': !isMobile,
        'mobile-table': isMobile,
        'mt-2 ': tabData.level === 2,
      }"
      fixed-header
      :headers="headers"
      :items="items"
      :loading="loading"
      :hide-default-header="isMobile"
      hide-default-footer
      disable-pagination
      must-sort
      item-key="id"
      :sort-by.sync="sortBy"
      :sort-desc.sync="descending"
    >
      <template #item.name="{ item }">
        <span v-if="tabData.level === 3" class="notranslate">
          Batch: {{ item.name }}
        </span>
        <span v-else class="text-break-all notranslate">
          {{ item.name }}
        </span>
        <v-btn icon @click="downloadReport(item)">
          <v-tooltip top color="primary">
            <template #activator="{ on }">
              <v-icon :color="tabData.fileData.iconColor" v-on="on">{{
                tabData.fileData.icon
              }}</v-icon>
            </template>
            <span>{{
              $t("download", { fileType: tabData.fileData.type })
            }}</span>
          </v-tooltip>
        </v-btn>
      </template>
      <template #item.merchantReference="{ item }">
        <span class="notranslate"> {{ item.merchantReference }}</span>
      </template>
      <template #item.targetDate="{ item }">
        <span class="text-no-wrap">
          {{ formattedDate(item.targetDate, "D MMM YYYY") }}</span
        >
      </template>
      <template #item.creationDate="{ item }">
        <span class="text-no-wrap">
          {{ formattedDate(item.creationDate, "D MMM H:mm:ss") }}</span
        >
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { format, addDays } from "date-fns";
import { axiosGet, axiosGetBlob } from "@/api/requests";
import isMobile from "@/mixins/isMobile";
import handleErrorStatus from "@/mixins/handleErrorStatus";
import snackBarMessages from "@/mixins/snackBarMessages";
import formattedDate from "@/mixins/formattedDate";
import ReportsToolbarDates from "./ReportsToolbarDates";
import { downloadBlobFile } from "@/helpers/downloadHelpers";

const defaultDate = format(addDays(new Date(), -6), "YYYY-MM-DD");

export default {
  name: "ReportsOverview",
  components: {
    ReportsToolbarDates,
  },
  mixins: [formattedDate, snackBarMessages, handleErrorStatus, isMobile],
  props: {
    tabData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      sortBy: "creationDate",
      descending: true,
    };
  },
  computed: {
    headers() {
      let headers = [
        {
          text: this.$t("name"),
          value: "name",
          divider: true,
          sortable: true,
        },
        {
          text: this.$t("reference"),
          value: "merchantReference",
          divider: true,
          sortable: true,
        },
        {
          text: this.$t("targetDate"),
          value: "targetDate",
          divider: true,
          sortable: true,
        },
        {
          text: this.$t("month"),
          value: "month",
          divider: true,
          sortable: true,
        },
        {
          text: this.$t("year"),
          value: "year",
          divider: true,
          sortable: true,
        },
        {
          text: this.$t("created"),
          value: "creationDate",
          divider: true,
          sortable: true,
        },
      ];
      switch (this.tabData.level) {
        case 1:
          headers = headers.filter((header) =>
            [
              "name",
              "merchantReference",
              "targetDate",
              "creationDate",
            ].includes(header.value)
          );
          break;
        case 2:
          headers = headers.filter((header) =>
            [
              "name",
              "merchantReference",
              "creationDate",
              "month",
              "year",
            ].includes(header.value)
          );

          break;
        case 3:
          headers = headers.filter((header) =>
            ["name", "merchantReference", "creationDate"].includes(header.value)
          );
          break;
      }
      return headers;
    },
    ...mapState(["currentHotel", "user", "connection"]),
  },
  created() {
    if (this.currentHotel) {
      this.getTableItems(defaultDate);
    }
  },
  methods: {
    getTableItems(startDate) {
      const endDate = format(addDays(startDate, 6), "YYYY-MM-DD");
      this.loading = true;
      this.tableItems = [];
      let url = `${process.env.VUE_APP_DATA_URL}/report/overview?level=${this.tabData.level}&name=${this.connection.code}&hotelcode=${this.currentHotel.hotelCode}`;
      if ([1, 3].includes(this.tabData.level)) {
        url += `&startdate=${startDate}&enddate=${endDate}`;
      }
      axiosGet(url, this.user.authToken)
        .then((response) => {
          if (response.status && response.status !== 200) {
            this.handleErrorStatus(response.status);
          } else if (response.success) {
            switch (this.tabData.level) {
              case 1:
                response.dailyReports.forEach((item) => {
                  item.name = item.pspReference;
                });
                this.items = response.dailyReports;
                this.setStartDateDF(startDate);
                break;
              case 2:
                response.monthlyReports.forEach((item) => {
                  item.name = item.pspReference;
                });
                this.items = response.monthlyReports;
                break;
              case 3:
                response.settlements.forEach((item) => {
                  item.name = item.batchNumber;
                });
                this.items = response.settlements;
                this.setStartDateSM(startDate);
                break;
            }
          } else {
            this.snackbarError(this.$t("loadingDataFailed"));
          }
        })
        .catch((error) => {
          console.log(error);
          this.snackbarError(this.$t("undefinedError"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    downloadReport(item) {
      this.$root.$loader.open(
        this.$t("download", { fileType: this.tabData.fileData.type })
      );
      let url = process.env.VUE_APP_DATA_URL + "/report";
      switch (this.tabData.level) {
        case 1:
        case 2:
          url +=
            "?name=" +
            this.connection.code +
            "&hotelcode=" +
            this.currentHotel.hotelCode +
            "&path=" +
            item.pspReference;
          break;
        case 3:
          url +=
            "/settlement?name=" +
            this.connection.code +
            "&hotelcode=" +
            this.currentHotel.hotelCode +
            "&batch=" +
            item.batchNumber;
          break;
      }
      axiosGetBlob(url, this.user.authToken)
        .then((response) => {
          if (response.status && response.status !== 200) {
            this.handleErrorStatus(response.status);
          } else if (response.size) {
            if (response.type === "application/pdf") {
              downloadBlobFile(response, item.pspReference);
            }
            if (
              response.type ===
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ) {
              const filename =
                this.currentHotel.hotelCode +
                "-Batch" +
                item.batchNumber +
                "_" +
                item.creationDate +
                ".xlsx";
              downloadBlobFile(response, filename);
            }
          } else {
            this.snackbarError(this.$t("loadingDataFailed"));
          }
        })
        .catch((error) => {
          console.log(error);
          this.snackbarError(this.$t("undefinedError"));
        })
        .finally(() => {
          this.$root.$loader.close();
        });
    },
    ...mapActions("reports", ["setStartDateDF", "setStartDateSM"]),
  },
};
</script>
